import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Scale, Compass, Building2, Check } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1758518727820-28491c194bee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGNvbnN1bHRpbmclMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc3ODEwNjQwNXww&ixlib=rb-4.1.0&q=85";

const iconMap = { scale: Scale, compass: Compass, building2: Building2 };

export default function Conseil() {
  const { t, lang } = useI18n();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", service_type: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", subject: "", message: "", service_type: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="conseil-page">
      <section className="pt-16">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
          <div className="eyebrow mb-4 rise">{lang === "fr" ? "Cabinet de conseil" : "Consulting firm"}</div>
          <h1 className="headline text-4xl sm:text-6xl text-[#1d1d1f] max-w-3xl rise rise-delay-1">
            {t.pages.consulting.title}
          </h1>
          <p className="text-[17px] sm:text-[20px] text-[#86868b] max-w-2xl mt-5 rise rise-delay-2">
            {t.pages.consulting.subtitle}
          </p>
        </div>
        <div className="mt-12 max-w-[1400px] mx-auto px-4">
          <div className="squircle relative aspect-[21/9] overflow-hidden bg-[#f5f5f7]">
            <img src={HERO} alt="Consulting" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="services-grid">
          {services.map((s) => {
            const Icon = iconMap[s.icon] || Scale;
            return (
              <div
                key={s.id}
                className="rounded-3xl bg-[#f5f5f7] p-10 hover:bg-[#ececf0] transition-colors"
                data-testid={`service-${s.id}`}
              >
                <Icon size={28} strokeWidth={1.5} className="text-[#1d1d1f] mb-6" />
                <h3 className="text-[22px] font-medium tracking-tight mb-3 text-[#1d1d1f]">
                  {lang === "fr" ? s.name : s.name_en}
                </h3>
                <p className="text-[14px] text-[#86868b] leading-relaxed">
                  {lang === "fr" ? s.description : s.description_en}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-24 bg-[#f5f5f7]">
        <div className="max-w-[900px] mx-auto px-6 sm:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">Contact</div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1d1d1f]">
              {t.contact.title}
            </h2>
            <p className="text-[17px] text-[#86868b] mt-3">{t.contact.subtitle}</p>
          </div>

          <form onSubmit={submit} className="bg-white rounded-3xl p-8 sm:p-12 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={t.contact.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" required />
              <Field label={t.contact.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" required />
              <Field label={t.contact.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
              <div>
                <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">{t.contact.service}</label>
                <select
                  value={form.service_type}
                  onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                  className="w-full bg-[#f5f5f7] rounded-2xl px-4 py-3 text-[14px] outline-none"
                  data-testid="contact-service-type"
                >
                  <option value="">—</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{lang === "fr" ? s.name : s.name_en}</option>
                  ))}
                </select>
              </div>
            </div>
            <Field label={t.contact.subject} value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" required />
            <div>
              <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">{t.contact.message}</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                className="w-full bg-[#f5f5f7] rounded-2xl px-4 py-3 text-[14px] outline-none resize-none"
                data-testid="contact-message"
              />
            </div>
            <button
              disabled={submitting}
              type="submit"
              className="btn-pill btn-primary text-[14px] disabled:opacity-50"
              data-testid="contact-submit"
            >
              {submitting ? "…" : t.contact.submit}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-[12px] uppercase tracking-wider text-[#86868b] mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full bg-[#f5f5f7] rounded-2xl px-4 py-3 text-[14px] outline-none"
        data-testid={testid}
      />
    </div>
  );
}
